import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { STORE_MAIN_ROOT_SLUGS } from "../../utils/storeMainRootSlugs";
import {
  resolveUrl,
  translateStoreCategory,
  localizedStoreProductName,
} from "../../utils/storeUtils";
import { useTranslation } from "react-i18next";
import { useStoreCart } from "./StoreCartContext";
import {
  HeroCarousel,
  OurCategories,
  WeeklyBestsellers,
  ProductCollections,
  QuickViewModal,
  StoreMegaMenu,
} from "./components";
import StoreGrainientBackdrop from "./components/StoreGrainientBackdrop";
import Seo from "../../components/Seo/Seo";
import "./Store.css";

function normalizeCategoryKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "");
}

function matchProductToCategory(product, categoryLabel) {
  if (!categoryLabel) return false;
  const cat = normalizeCategoryKey(product?.category);
  const slug = normalizeCategoryKey(categoryLabel);
  // Collapse "Armchairs" into "Chairs"
  if (slug === "chairs" || slug === "chair") {
    return cat === "chairs" || cat === "chair" || cat === "armchairs" || cat === "armchair";
  }
  if (slug === "doors" || slug === "door") {
    const fireDoor = cat.includes("fire") && (cat.includes("door") || cat.includes("rated"));
    if (fireDoor) return false;
    return cat === "doors" || cat === "door" || (cat.includes("door") && !cat.includes("fire"));
  }
  if (slug === "firerateddoors") {
    return cat.includes("fire") && cat.includes("door");
  }
  return cat === slug || cat.includes(slug);
}

function labelToSlug(label) {
  return String(label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function resolveCategoryAsset(url, publicBase) {
  if (!url || typeof url !== "string") return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${publicBase}${url}`;
  return `${publicBase}/${url}`.replace(/([^:]\/)\/+/g, "$1");
}

/** Fallbacks if `store.heroSlides.<category>` keys are missing */
const HERO_SLIDE_DEFAULTS = {
  default: {
    title: "Modern Wooden Products",
    subtitle: "Premium craftsmanship for homes and hospitality.",
  },
  sofas: {
    title: "Modern Residential Furniture",
    subtitle: "Warm tones, natural wood, and upholstery made for everyday living.",
  },
  chairs: {
    title: "Upholstered Chair Collection",
    subtitle: "Discover premium seating for modern spaces.",
  },
  tables: {
    title: "Hospitality Signature Pieces",
    subtitle: "Built for style, comfort, and durability.",
  },
};

function Store() {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const {
    addToCart: addToCartContext,
    toggleWishlist,
    isInWishlist,
  } = useStoreCart();
  const publicBase = process.env.PUBLIC_URL || "";
  const apiBaseUrl = axios.defaults.baseURL || "";
  const resolveProductUrl = useCallback(
    (url) => resolveUrl(url, apiBaseUrl),
    [apiBaseUrl]
  );

  // Fix mobile hero stretching: capture viewport height once before any scroll/address-bar change
  useEffect(() => {
    const setMobileHeroHeight = () => {
      document.documentElement.style.setProperty("--mobile-hero-h", `${window.innerHeight}px`);
    };
    setMobileHeroHeight();
    window.addEventListener("orientationchange", () => setTimeout(setMobileHeroHeight, 120));
    return () => window.removeEventListener("orientationchange", setMobileHeroHeight);
  }, []);

  const [products, setProducts] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [collectionsConfig, setCollectionsConfig] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [heroMegaOpen, setHeroMegaOpen] = useState(false);
  const [heroMegaRootSlug, setHeroMegaRootSlug] = useState("");
  const heroMegaCloseTimerRef = useRef(null);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  // Search is handled in the store header (query param `q`)

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, categoriesResponse, collectionsResponse, treeResponse] = await Promise.all([
          axios.get("/store/products"),
          axios.get("/store/categories"),
          axios.get("/store/products/collections-config").catch(() => ({ data: null })),
          axios.get("/store/categories/tree").catch(() => ({ data: [] })),
        ]);
        setProducts(Array.isArray(productsResponse.data) ? productsResponse.data : []);
        setStoreCategories(
          Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []
        );
        setCollectionsConfig(collectionsResponse?.data || null);
        setCategoryTree(Array.isArray(treeResponse.data) ? treeResponse.data : []);
      } catch (error) {
        toast.error(t("store.failedToLoadStoreData", { defaultValue: "Failed to load store data." }));
      }
    };
    fetchData();
  }, []);

  const addToCart = useCallback(
    (product, options = {}) => {
      addToCartContext(product, options);
      const label = localizedStoreProductName(product, i18n.language);
      toast.success(
        label
          ? t("store.toastProductAddedToCart", {
              name: label,
              defaultValue: "{{name}} added to cart.",
            })
          : t("store.toastProductAddedToCartGeneric", {
              defaultValue: "Product added to cart.",
            })
      );
    },
    [addToCartContext, i18n.language, t]
  );

  const heroSlides = useMemo(() => {
    const heroConfigMap = new Map(
      Array.isArray(collectionsConfig?.heroSlides)
        ? collectionsConfig.heroSlides.map((s) => {
            const cat = String(s?.category || "").toLowerCase();
            return [cat, {
              desktop: String(s?.backgroundUrl || "").trim(),
              mobile: String(s?.backgroundUrlMobile || "").trim(),
              desktopAr: String(s?.backgroundUrlAr || "").trim(),
              mobileAr: String(s?.backgroundUrlArMobile || "").trim(),
              productImageUrl: String(s?.productImageUrl || "").trim(),
              heroProductId: String(s?.heroProductId || "").trim(),
              heroProductItems: Array.isArray(s?.heroProductItems) ? s.heroProductItems : [],
              heroProductX: Number(s?.heroProductX ?? 0) || 0,
              heroProductY: Number(s?.heroProductY ?? 0) || 0,
              heroProductScale: Math.max(0.1, Number(s?.heroProductScale ?? 1) || 1),
              heroBtnX: Number(s?.heroBtnX ?? 0) || 0,
              heroBtnY: Number(s?.heroBtnY ?? 0) || 0,
              heroBtnMobileX: Number(s?.heroBtnMobileX ?? 0) || 0,
              heroBtnMobileY: Number(s?.heroBtnMobileY ?? 0) || 0,
              heroProductMobileX: Number(s?.heroProductMobileX ?? 0) || 0,
              heroProductMobileY: Number(s?.heroProductMobileY ?? 0) || 0,
              heroProductMobileScale: Math.max(0.1, Number(s?.heroProductMobileScale ?? 1) || 1),
              heroTextX: Number(s?.heroTextX ?? 0) || 0,
              heroTextY: Number(s?.heroTextY ?? 0) || 0,
              heroProductXAr: Number(s?.heroProductXAr ?? 0) || 0,
              heroProductYAr: Number(s?.heroProductYAr ?? 0) || 0,
              heroProductScaleAr: Math.max(0.1, Number(s?.heroProductScaleAr ?? 1) || 1),
              heroTextXAr: Number(s?.heroTextXAr ?? 0) || 0,
              heroTextYAr: Number(s?.heroTextYAr ?? 0) || 0,
              heroBtnXAr: Number(s?.heroBtnXAr ?? 0) || 0,
              heroBtnYAr: Number(s?.heroBtnYAr ?? 0) || 0,
              heroProductMobileXAr: Number(s?.heroProductMobileXAr ?? 0) || 0,
              heroProductMobileYAr: Number(s?.heroProductMobileYAr ?? 0) || 0,
              heroProductMobileScaleAr: Math.max(0.1, Number(s?.heroProductMobileScaleAr ?? 1) || 1),
              heroBtnMobileXAr: Number(s?.heroBtnMobileXAr ?? 0) || 0,
              heroBtnMobileYAr: Number(s?.heroBtnMobileYAr ?? 0) || 0,
              heroTitleEn: String(s?.heroTitleEn || "").trim(),
              heroTitleAr: String(s?.heroTitleAr || "").trim(),
              heroSubtitleEn: String(s?.heroSubtitleEn || "").trim(),
              heroSubtitleAr: String(s?.heroSubtitleAr || "").trim(),
              heroTrustItems: Array.isArray(s?.heroTrustItems) ? s.heroTrustItems : [],
              heroDotsX: Number(s?.heroDotsX ?? 0) || 0,
              heroDotsY: s?.heroDotsY != null ? Number(s.heroDotsY) : 16,
              heroDotsMobileX: Number(s?.heroDotsMobileX ?? 0) || 0,
              heroDotsMobileY: s?.heroDotsMobileY != null ? Number(s.heroDotsMobileY) : 10,
            }];
          })
        : []
    );
    // Static hero uses ONE background image + product: category key "default".
    const defs = [
      {
        category: "default",
        theme: "cream",
      },
    ];
    return defs.map((d) => {
      const cfg = heroConfigMap.get(d.category) || {};
      const fallback =
        HERO_SLIDE_DEFAULTS[d.category] ||
        HERO_SLIDE_DEFAULTS.default ||
        HERO_SLIDE_DEFAULTS.sofas;
      return {
        ...d,
        background: cfg.desktop || "",
        backgroundMobile: cfg.mobile || "",
        backgroundAr: cfg.desktopAr || "",
        backgroundArMobile: cfg.mobileAr || "",
        productImageUrl: cfg.productImageUrl || "",
        heroProductId: cfg.heroProductId || "",
        heroProductItems: cfg.heroProductItems || [],
        heroProductX: cfg.heroProductX || 0,
        heroProductY: cfg.heroProductY || 0,
        heroProductScale: cfg.heroProductScale || 1,
        heroBtnX: cfg.heroBtnX || 0,
        heroBtnY: cfg.heroBtnY || 0,
        heroBtnMobileX: cfg.heroBtnMobileX || 0,
        heroBtnMobileY: cfg.heroBtnMobileY || 0,
        heroTrustItems: cfg.heroTrustItems || [],
        heroProductMobileX: cfg.heroProductMobileX || 0,
        heroProductMobileY: cfg.heroProductMobileY || 0,
        heroProductMobileScale: cfg.heroProductMobileScale || 1,
        heroTextX: cfg.heroTextX || 0,
        heroTextY: cfg.heroTextY || 0,
        heroProductXAr: cfg.heroProductXAr || 0,
        heroProductYAr: cfg.heroProductYAr || 0,
        heroProductScaleAr: cfg.heroProductScaleAr || 1,
        heroTextXAr: cfg.heroTextXAr || 0,
        heroTextYAr: cfg.heroTextYAr || 0,
        heroBtnXAr: cfg.heroBtnXAr || 0,
        heroBtnYAr: cfg.heroBtnYAr || 0,
        heroProductMobileXAr: cfg.heroProductMobileXAr || 0,
        heroProductMobileYAr: cfg.heroProductMobileYAr || 0,
        heroProductMobileScaleAr: cfg.heroProductMobileScaleAr || 1,
        heroBtnMobileXAr: cfg.heroBtnMobileXAr || 0,
        heroBtnMobileYAr: cfg.heroBtnMobileYAr || 0,
        heroTitleEn: cfg.heroTitleEn || "",
        heroTitleAr: cfg.heroTitleAr || "",
        heroSubtitleEn: cfg.heroSubtitleEn || "",
        heroSubtitleAr: cfg.heroSubtitleAr || "",
        heroDotsX: cfg.heroDotsX || 0,
        heroDotsY: cfg.heroDotsY != null ? cfg.heroDotsY : 16,
        heroDotsMobileX: cfg.heroDotsMobileX || 0,
        heroDotsMobileY: cfg.heroDotsMobileY != null ? cfg.heroDotsMobileY : 10,
        title: t(`store.heroSlides.${d.category}.title`, {
          defaultValue: fallback.title,
        }),
        subtitle: t(`store.heroSlides.${d.category}.subtitle`, {
          defaultValue: fallback.subtitle,
        }),
      };
    });
  }, [collectionsConfig?.heroSlides, t]);

  const heroProducts = useMemo(() => {
    const items = heroSlides[0]?.heroProductItems || [];
    if (items.length) {
      const picked = items.map((item) => products.find((p) => String(p?._id) === item.productId)).filter(Boolean);
      if (picked.length) return picked;
    }
    const heroProductId = heroSlides[0]?.heroProductId;
    if (heroProductId) {
      const picked = products.find((p) => String(p?._id) === heroProductId);
      if (picked) return [picked];
    }
    const enabled = products
      .filter((p) => p?.heroEnabled)
      .sort((a, b) => Number(a?.heroOrder || 0) - Number(b?.heroOrder || 0));
    if (enabled.length > 0) return enabled.slice(0, 1);
    return products.slice(0, 1);
  }, [products, heroSlides]);

  const categoryItems = useMemo(() => {
    const source = storeCategories.length > 0 ? storeCategories : [];
    const sorted = [...source].sort(
      (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
    );
    const mapped = sorted.map((item) => ({
      label: item.label,
      slug: item.slug || labelToSlug(item.label),
      // Only ROOT categories should show icons in UI (subcategories can still store iconUrl for admin).
      iconSvg: item?.parentId ? "" : resolveCategoryAsset(item.iconUrl, publicBase),
      image: resolveCategoryAsset(
        item.storeHomeImageUrl || item.imageUrl,
        publicBase
      ),
    }));
    const filtered = mapped.filter((c) => {
      const s = String(c.slug || "").toLowerCase();
      return true;
    });
    const cover =
      filtered.find((c) => c.image)?.image ||
      resolveCategoryAsset("/photos/wd-furniture-pt-sofas-opt.webp", publicBase);
    const allItem = {
      label: "All items",
      slug: "all",
      iconSvg: "",
      icon: "fa-table-cells-large",
      image: cover,
    };
    return [allItem, ...filtered.filter((c) => c.slug !== "all")];
  }, [storeCategories, publicBase]);

  const ourCategories = useMemo(
    () =>
      categoryItems.map((category) => ({
        label: category.label,
        slug: category.slug,
        image: category.image,
        productCount:
          category.slug === "all"
            ? products.length
            : products.filter((p) => matchProductToCategory(p, category.label)).length,
      })),
    [categoryItems, products]
  );

  const handleCategoryClick = useCallback(
    (categoryLabel) => {
      if (!categoryLabel) return;
      const found = categoryItems.find((item) => item.label === categoryLabel);
      const categorySlug = found?.slug || labelToSlug(categoryLabel);
      setSelectedCategory(categoryLabel);
      navigate(`/store/category/${categorySlug}`);
    },
    [navigate, categoryItems]
  );

  const mainCategoryRoots = useMemo(() => {
    const list = Array.isArray(categoryTree) ? categoryTree : [];
    const bySlug = new Map(list.map((n) => [String(n?.slug || "").toLowerCase(), n]));
    const ordered = STORE_MAIN_ROOT_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
    if (ordered.length > 0) return ordered;
    return list;
  }, [categoryTree]);

  const cancelHeroMegaClose = useCallback(() => {
    if (heroMegaCloseTimerRef.current) {
      clearTimeout(heroMegaCloseTimerRef.current);
      heroMegaCloseTimerRef.current = null;
    }
  }, []);

  const scheduleHeroMegaClose = useCallback((delayMs = 160) => {
    cancelHeroMegaClose();
    heroMegaCloseTimerRef.current = setTimeout(() => {
      setHeroMegaOpen(false);
      heroMegaCloseTimerRef.current = null;
    }, delayMs);
  }, [cancelHeroMegaClose]);

  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  // Main categories icons:
  // - Primary: admin-controlled `iconUrl` on the category (SVG/PNG/etc.)
  // - Fallback: current FontAwesome defaults (so existing look stays if iconUrl is missing/bad).
  // NOTE: `iconUrl` is required in the current DB schema and many categories have legacy/seed icons.
  // We only treat `iconUrl` as "admin-controlled main icon" when it is NOT a known legacy icon.
  const isLegacySeedIconUrl = useCallback((url) => {
    const u = String(url || "").trim();
    if (!u) return true;
    // Legacy furniture icon set + old chair icon (used as generic defaults in seed/migrations)
    if (u === "/photos/Chair.svg") return true;
    if (u.startsWith("/photos/wd-furniture-")) return true;
    return false;
  }, []);
  const defaultIconForRoot = useCallback((slug) => {
    const s = String(slug || "").toLowerCase();
    switch (s) {
      case "furniture":
        return "fa-couch";
      case "doors":
        return "fa-door-open";
      case "kitchens":
        return "fa-utensils";
      case "decor-accessories":
        return "fa-gem";
      case "outdoor":
        return "fa-tree";
      case "curtains":
        return "fa-window-maximize";
      default:
        return "fa-shapes";
    }
  }, []);

  return (
    <>
      <Seo
        title="المتجر"
        description="تسوّق أثاث ومنتجات خشبية من الرملاء — فئات متنوعة، جودة عالية، وتوصيل لجميع مناطق السعودية."
        path="/store"
      />
      <section className="store-page">
      <StoreGrainientBackdrop />
      <div className="container">
        <section className="store-hero">
          {mainCategoryRoots.length > 0 ? (
            <div
              className={`store-category-row-wrap store-category-row-wrap--floating${
                heroMegaOpen ? " is-mega-open" : ""
              }`}
              onMouseEnter={cancelHeroMegaClose}
              onMouseLeave={() => scheduleHeroMegaClose(160)}
            >
              <div
                className="store-category-row store-category-row--nav"
                dir={isRtl ? "rtl" : "ltr"}
              >
                {mainCategoryRoots.map((root) => {
                  const rootSlug = String(root.slug || "").toLowerCase();
                  const megaOpenForRoot = heroMegaOpen && heroMegaRootSlug === rootSlug;
                  const rawIconUrl = String(root?.iconUrl || "").trim();
                  const iconUrl = !isLegacySeedIconUrl(rawIconUrl)
                    ? resolveCategoryAsset(rawIconUrl, publicBase)
                    : "";
                  return (
                    <button
                      type="button"
                      key={root._id || root.slug || root.label}
                      className={`category-link-btn${megaOpenForRoot ? " category-link-btn--open" : ""}`}
                      aria-expanded={megaOpenForRoot}
                      aria-haspopup="menu"
                      onMouseEnter={() => {
                        cancelHeroMegaClose();
                        setHeroMegaRootSlug(rootSlug);
                        setHeroMegaOpen(true);
                      }}
                      onFocus={() => {
                        cancelHeroMegaClose();
                        setHeroMegaRootSlug(rootSlug);
                        setHeroMegaOpen(true);
                      }}
                      onClick={() => navigate(`/store/category/${rootSlug}`)}
                    >
                      {iconUrl ? (
                        <img
                          className="category-link-btn-icon"
                          src={iconUrl}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                        />
                      ) : (
                        <i
                          className={`fa-solid ${defaultIconForRoot(rootSlug)}`}
                          aria-hidden="true"
                        />
                      )}
                      <span>{translateStoreCategory(t, root)}</span>
                    </button>
                  );
                })}
              </div>

              {heroMegaOpen && categoryTree.length > 0 ? (
                <div
                  className="store-hero-mega-panel"
                  role="menu"
                  onMouseEnter={cancelHeroMegaClose}
                  onMouseLeave={() => scheduleHeroMegaClose(120)}
                >
                  <StoreMegaMenu
                    tree={categoryTree}
                    isRtl={(i18n.language || "").toLowerCase().startsWith("ar")}
                    activeRootSlug={heroMegaRootSlug}
                    showRootsColumn={false}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <HeroCarousel
            slides={heroSlides}
            products={heroProducts}
            onAddToCart={addToCart}
            onCategoryClick={handleCategoryClick}
            staticHero
            resolveUrl={resolveProductUrl}
          />
        </section>

        <OurCategories
          categories={ourCategories}
          photosBasePath={`${publicBase}/photos`}
          selectedCategory={selectedCategory}
          onCategoryClick={handleCategoryClick}
        />

        <WeeklyBestsellers
          products={products}
          resolveUrl={resolveProductUrl}
          onAddToCart={addToCart}
          onToggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
          onQuickView={setQuickViewProduct}
        />

        <ProductCollections
          photosBasePath={`${publicBase}/photos`}
          collectionsConfig={collectionsConfig}
          resolveImageUrl={resolveProductUrl}
          onAddToCart={addToCart}
          catalogProducts={products}
        />
      </div>
      <QuickViewModal
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={addToCart}
        resolveUrl={resolveProductUrl}
      />
    </section>
    </>
  );
}

export default Store;
